import { useMemo } from "react";
import { motion } from "framer-motion";
import { DollarSign, TrendingUp, ShoppingCart, BarChart3, Timer } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend
} from "recharts";
import type { Tables } from "@/integrations/supabase/types";

type SongOrder = Tables<"song_orders">;

const PLAN_PRICES: Record<string, number> = {
  essentials: 35,
  premium: 85,
};

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const CHART_COLORS = [
  "hsl(var(--primary))",
  "hsl(var(--accent))",
  "hsl(var(--muted-foreground))",
  "hsl(var(--secondary))",
];

interface SalesDashboardProps {
  orders: SongOrder[];
}

const SalesDashboard = ({ orders }: SalesDashboardProps) => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  const stats = useMemo(() => {
    const ytdOrders = orders.filter((o) => new Date(o.created_at).getFullYear() === currentYear);
    const thisMonthOrders = ytdOrders.filter((o) => new Date(o.created_at).getMonth() === currentMonth);
    const lastMonthOrders = ytdOrders.filter((o) => new Date(o.created_at).getMonth() === currentMonth - 1);

    const getRevenue = (list: SongOrder[]) =>
      list.reduce((sum, o) => sum + (o.price_paid && Number(o.price_paid) > 0 ? Number(o.price_paid) : 0), 0);

    const ytdRevenue = getRevenue(ytdOrders);
    const thisMonthRevenue = getRevenue(thisMonthOrders);
    const lastMonthRevenue = getRevenue(lastMonthOrders);
    const revenueChange = lastMonthRevenue > 0
      ? Math.round(((thisMonthRevenue - lastMonthRevenue) / lastMonthRevenue) * 100)
      : thisMonthRevenue > 0 ? 100 : 0;

    // Average turnaround time (submitted → completed)
    const completedOrders = orders.filter((o) => o.status === "completed");
    let avgTurnaround = 0;
    if (completedOrders.length > 0) {
      const totalDays = completedOrders.reduce((sum, o) => {
        const created = new Date(o.created_at).getTime();
        const updated = new Date(o.updated_at).getTime();
        return sum + (updated - created) / (1000 * 60 * 60 * 24);
      }, 0);
      avgTurnaround = Math.round(totalDays / completedOrders.length);
    }

    return { ytdOrders: ytdOrders.length, ytdRevenue, thisMonthRevenue, thisMonthOrders: thisMonthOrders.length, revenueChange, avgTurnaround, completedCount: completedOrders.length };
  }, [orders, currentYear, currentMonth]);

  const monthlyData = useMemo(() => {
    const months: { name: string; revenue: number; orders: number }[] = [];
    for (let m = 0; m < 12; m++) {
      const monthOrders = orders.filter((o) => {
        const d = new Date(o.created_at);
        return d.getFullYear() === currentYear && d.getMonth() === m;
      });
      months.push({
        name: MONTH_NAMES[m],
        revenue: monthOrders.reduce((sum, o) => sum + (o.price_paid && Number(o.price_paid) > 0 ? Number(o.price_paid) : 0), 0),
        orders: monthOrders.length,
      });
    }
    return months;
  }, [orders, currentYear]);

  const planBreakdown = useMemo(() => {
    const ytdOrders = orders.filter((o) => new Date(o.created_at).getFullYear() === currentYear);
    const essentials = ytdOrders.filter((o) => o.plan_type === "essentials").length;
    const premium = ytdOrders.filter((o) => o.plan_type === "premium").length;
    return [
      { name: "Essentials", value: essentials },
      { name: "Premium", value: premium },
    ].filter((d) => d.value > 0);
  }, [orders, currentYear]);

  const eventBreakdown = useMemo(() => {
    const ytdOrders = orders.filter((o) => new Date(o.created_at).getFullYear() === currentYear);
    const counts: Record<string, number> = {};
    ytdOrders.forEach((o) => {
      counts[o.event_type] = (counts[o.event_type] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([name, value]) => ({ name: name.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase()), value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);
  }, [orders, currentYear]);

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: "YTD Revenue", value: `$${stats.ytdRevenue.toLocaleString()}`, icon: DollarSign },
          { label: "This Month", value: `$${stats.thisMonthRevenue.toLocaleString()}`, icon: TrendingUp, sub: stats.revenueChange !== 0 ? `${stats.revenueChange > 0 ? "+" : ""}${stats.revenueChange}% vs last month` : undefined },
          { label: "YTD Orders", value: stats.ytdOrders, icon: ShoppingCart },
          { label: "Avg Turnaround", value: stats.completedCount > 0 ? `${stats.avgTurnaround}d` : "—", icon: Timer, sub: stats.completedCount > 0 ? `From ${stats.completedCount} completed` : "No completed orders" },
        ].map((kpi, i) => (
          <motion.div key={kpi.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            className="bg-card rounded-xl p-6 shadow-soft">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <kpi.icon className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{kpi.value}</p>
                <p className="text-muted-foreground text-sm">{kpi.label}</p>
                {kpi.sub && (
                  <p className={`text-xs mt-0.5 ${stats.revenueChange >= 0 ? "text-primary" : "text-destructive"}`}>
                    {kpi.sub}
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Monthly Revenue Chart */}
      <div className="bg-card rounded-2xl shadow-soft p-6">
        <h3 className="font-heading text-lg font-semibold text-foreground mb-4">
          Monthly Revenue — {currentYear}
        </h3>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="name" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }} />
              <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }} tickFormatter={(v) => `$${v}`} />
              <Tooltip
                contentStyle={{
                  background: "hsl(var(--card))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: "8px",
                  color: "hsl(var(--foreground))",
                }}
                formatter={(value: number) => [`$${value}`, "Revenue"]}
              />
              <Bar dataKey="revenue" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Monthly Orders Chart */}
      <div className="bg-card rounded-2xl shadow-soft p-6">
        <h3 className="font-heading text-lg font-semibold text-foreground mb-4">
          Monthly Orders — {currentYear}
        </h3>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="name" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }} />
              <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }} allowDecimals={false} />
              <Tooltip
                contentStyle={{
                  background: "hsl(var(--card))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: "8px",
                  color: "hsl(var(--foreground))",
                }}
              />
              <Bar dataKey="orders" fill="hsl(var(--accent))" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Breakdowns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Plan Breakdown */}
        <div className="bg-card rounded-2xl shadow-soft p-6">
          <h3 className="font-heading text-lg font-semibold text-foreground mb-4">Plan Breakdown (YTD)</h3>
          {planBreakdown.length === 0 ? (
            <p className="text-muted-foreground text-sm text-center py-8">No data yet</p>
          ) : (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={planBreakdown} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                    {planBreakdown.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Legend />
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Event Type Breakdown */}
        <div className="bg-card rounded-2xl shadow-soft p-6">
          <h3 className="font-heading text-lg font-semibold text-foreground mb-4">Top Event Types (YTD)</h3>
          {eventBreakdown.length === 0 ? (
            <p className="text-muted-foreground text-sm text-center py-8">No data yet</p>
          ) : (
            <div className="space-y-3">
              {eventBreakdown.map((e, i) => {
                const maxVal = eventBreakdown[0].value;
                return (
                  <div key={e.name}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-foreground font-medium">{e.name}</span>
                      <span className="text-muted-foreground">{e.value} orders</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <motion.div
                        className="h-full rounded-full"
                        style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }}
                        initial={{ width: 0 }}
                        animate={{ width: `${(e.value / maxVal) * 100}%` }}
                        transition={{ delay: i * 0.1, duration: 0.5 }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SalesDashboard;
